import { Camera } from "lucide-react";
import { dateRange } from "@/components/album/ui";
import { typeOf } from "@/config/album";
import { cn } from "@/lib/utils";

export const TEXT_COLORS = [
  { id: "white", label: "Putih", value: "#FFFFFF" },
  { id: "ivory", label: "Krem", value: "#F3EBDD" },
  { id: "gold", label: "Emas", value: "#E6C98A" },
  { id: "charcoal", label: "Hitam", value: "#1C1C1C" },
];
export const OVERLAYS = [
  { id: "none", label: "Tanpa", v: 0 },
  { id: "soft", label: "Tipis", v: 0.25 },
  { id: "medium", label: "Sedang", v: 0.45 },
  { id: "strong", label: "Pekat", v: 0.65 },
];
export const isLight = (hex = "#FFFFFF") => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.6;
};

const SIZE = {
  full: { pad: "px-7 py-8", wm: "text-sm", label: "text-lg", title: "text-5xl", names: "text-3xl", date: "text-xs", gap: "mt-10", btn: "py-4 text-sm", box: "h-14 w-14", num: "text-2xl", sub: "text-[9px]", link: "text-xs", input: "pb-2 text-xl", hint: "text-[10px]" },
  preview: { pad: "px-5 py-6", wm: "text-[9px]", label: "text-[12px]", title: "text-[31px]", names: "text-[19px]", date: "text-[9px]", gap: "mt-5", btn: "py-2.5 text-[9px]", box: "h-9 w-9", num: "text-base", sub: "text-[6px]", link: "text-[8px]", input: "pb-1 text-[11px]", hint: "text-[6px]" },
};

// guestName / onName: isian nama tamu (hanya di halaman tamu). Pilihan galeri sudah dihapus.
export default function CoverView({ data, size = "full", left = 25, busy, guestName = "", onName, onCamera, onView }) {
  const s = SIZE[size];
  const live = size === "full";
  const has = !!data.cover;
  const et = typeOf(data.eventType);
  const color = has ? data.textColor || "#FFFFFF" : "#1C1C1C";
  const light = isLight(color);
  const a = (OVERLAYS.find((o) => o.id === (data.overlay || "medium")) || OVERLAYS[2]).v;
  const rgb = light ? "0,0,0" : "255,255,255";
  const overlay = has && a > 0 ? `linear-gradient(to bottom, rgba(${rgb},${a * 0.55}), rgba(${rgb},${a * 0.45}) 35%, rgba(${rgb},${a}))` : "none";
  const btnText = light ? "#1C1C1C" : "#FFFFFF";
  const dates = data.startDate ? dateRange(data) : "";
  const nameOk = guestName.trim().length >= 2;

  // Teks mengikuti jenis acara. Hanya pernikahan yang memakai kata "pengantin".
  const label = et.id === "other" ? (data.couple ? `Diundang oleh ${data.couple}` : "Kamu diundang") : et.coverLabel;
  const showName = et.id !== "other" && (data.couple || !live);
  const ph = (v, t) => (v ? v : <span className="opacity-40">{t}</span>);

  return (
    <div
      className={cn("relative isolate flex flex-col overflow-hidden bg-white", live ? "min-h-screen" : "h-full w-full")}
      style={{ color, textShadow: has && light ? "0 1px 14px rgba(0,0,0,.3)" : "none" }}
    >
      {has && <img src={data.cover} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />}
      <div className="absolute inset-0 -z-10" style={{ background: overlay }} />

      <div className={cn("flex flex-1 flex-col justify-between text-center", s.pad)}>
        <p className={cn("font-sans uppercase tracking-[0.5em]", s.wm)}>RUANG KENANG</p>

        <div className="flex flex-col items-center">
          {label && <p className={cn("font-serif italic opacity-90", s.label)}>{label}</p>}
          <h1 className={cn("mt-2 font-serif font-light leading-[1.05] tracking-tight", s.title)}>
            {ph(data.eventName, "Nama acaramu")}
          </h1>
          {showName && (
            <p className={cn("mt-3 font-serif italic", s.names)}>{ph(data.couple, et.coverName)}</p>
          )}
          <p className={cn("mt-3 font-sans uppercase tracking-widest opacity-80", s.date)}>
            {dates || <span className="opacity-60">Tanggal acara</span>}
          </p>

          <div className={cn("flex w-full flex-col items-center", s.gap, !live && "pointer-events-none")} aria-hidden={!live}>
            {/* 1. isi nama dulu */}
            {live ? (
              <input
                type="text"
                value={guestName}
                onChange={(e) => onName && onName(e.target.value)}
                maxLength={40}
                placeholder="Nama kamu"
                aria-label="Nama kamu"
                autoComplete="name"
                data-testid="guest-name"
                className={cn("w-full max-w-[300px] border-b bg-transparent text-center font-serif italic outline-none placeholder:text-current placeholder:opacity-60", s.input)}
                style={{ borderColor: color, color, textShadow: "none" }}
              />
            ) : (
              <div className={cn("w-full max-w-[300px] border-b text-center font-serif italic opacity-70", s.input)} style={{ borderColor: color + "88" }}>
                Nama kamu
              </div>
            )}
            <p className={cn("mt-2 font-sans uppercase tracking-widest opacity-70", s.hint)}>
              {live && !nameOk ? "Tulis namamu dulu untuk mulai" : "Namamu tampil di album pemilik acara"}
            </p>

            {/* 2. baru tombol ambil foto */}
            <div className={cn("flex w-full items-center justify-center gap-3", live ? "mt-5" : "mt-3")}>
              <button type="button" tabIndex={live ? 0 : -1} onClick={onCamera} disabled={live && (busy || left === 0 || !nameOk)} data-testid="btn-camera"
                className={cn("inline-flex max-w-[220px] flex-1 items-center justify-center gap-2 font-sans uppercase tracking-widest disabled:opacity-40", s.btn)}
                style={{ background: color, color: btnText, textShadow: "none" }}>
                <Camera className={live ? "h-4 w-4" : "h-2.5 w-2.5"} /> {busy ? "Mengirim…" : "Ambil Foto"}
              </button>
              <div className={cn("flex flex-col items-center justify-center border", s.box)} style={{ borderColor: color + "88", background: light ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.05)" }} data-testid="quota" aria-label={`Sisa jatah ${left} foto`}>
                <span className={cn("font-serif leading-none", s.num)}>{left}</span>
                <span className={cn("font-sans uppercase tracking-wider opacity-80", s.sub)}>sisa</span>
              </div>
            </div>
            <button type="button" tabIndex={live ? 0 : -1} onClick={onView} data-testid="btn-view-album"
              className={cn("mt-4 font-sans uppercase tracking-widest underline underline-offset-4", s.link)}>Lihat Album</button>
          </div>
        </div>
      </div>
    </div>
  );
}
